/**
 * FieldSourceEditor — per-target-field source control.
 *
 * The default is a single header <Select> (1:1 or Skip). The "More"
 * popover unlocks the two structural transforms:
 *   - Combine columns (N:1): pick 2-3 headers + a separator.
 *   - Use part of a column (1:N): pick a header, a delimiter, and which
 *     part ("1st part" / "2nd part" / "everything after the 1st part").
 * A live example rendered from the first CSV row shows the result of
 * whatever is configured, so non-technical users see the effect
 * immediately.
 */

import { useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { resolveCellValue, type ColumnSource } from '../../lib/csv-mapping';

const SKIP = '__skip__';

interface Props {
  fieldLabel: string;
  headers: string[];
  source: ColumnSource;
  sampleRow: Record<string, string> | null;
  onChange: (source: ColumnSource) => void;
}

function HeaderSelect({
  headers,
  value,
  placeholder,
  ariaLabel,
  onChange,
}: {
  headers: string[];
  value: string | null;
  placeholder: string;
  ariaLabel: string;
  onChange: (header: string | null) => void;
}) {
  return (
    <Select value={value ?? SKIP} onValueChange={(v) => onChange(v === SKIP ? null : v)}>
      <SelectTrigger className="min-w-[10rem]" aria-label={ariaLabel}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SKIP}>— Skip —</SelectItem>
        {headers.map((h) => (
          <SelectItem key={h} value={h}>
            {h}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function FieldSourceEditor({ fieldLabel, headers, source, sampleRow, onChange }: Props) {
  const [open, setOpen] = useState(false);

  const example = sampleRow ? resolveCellValue(sampleRow, source) : '';
  const isAdvanced = source.kind === 'join' || source.kind === 'split';

  return (
    <div className="flex items-center gap-2">
      {!isAdvanced && (
        <HeaderSelect
          headers={headers}
          value={source.kind === 'column' ? source.header : null}
          placeholder="— Skip —"
          ariaLabel={`Column for ${fieldLabel}`}
          onChange={(header) => onChange(header ? { kind: 'column', header } : { kind: 'none' })}
        />
      )}
      {isAdvanced && (
        <span className="rounded border border-border bg-muted/40 px-2 py-1.5 text-xs">
          {source.kind === 'join'
            ? `Combined: ${source.headers.join(' + ')}`
            : `Part of: ${source.header}`}
        </span>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-muted-foreground"
            aria-label={`Advanced mapping for ${fieldLabel}`}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 flex flex-col gap-3">
          <p className="text-sm font-medium">{fieldLabel}: advanced mapping</p>

          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Mode</span>
            <Select
              value={source.kind === 'join' || source.kind === 'split' ? source.kind : 'column'}
              onValueChange={(mode) => {
                if (mode === 'join') {
                  onChange({
                    kind: 'join',
                    headers: source.kind === 'column' ? [source.header] : [],
                    separator: ' ',
                  });
                } else if (mode === 'split') {
                  onChange({
                    kind: 'split',
                    header:
                      source.kind === 'column'
                        ? source.header
                        : source.kind === 'split'
                          ? source.header
                          : (headers[0] ?? ''),
                    delimiter: ' ',
                    partIndex: 0,
                    takeRemainder: false,
                  });
                } else {
                  onChange({ kind: 'none' });
                }
              }}
            >
              <SelectTrigger aria-label={`Mapping mode for ${fieldLabel}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="column">One column</SelectItem>
                <SelectItem value="join">Combine columns</SelectItem>
                <SelectItem value="split">Use part of a column</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {source.kind === 'join' && (
            <div className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <HeaderSelect
                  key={i}
                  headers={headers}
                  value={source.headers[i] ?? null}
                  placeholder={i === 0 ? 'First column' : 'Add a column…'}
                  ariaLabel={`Combine column ${i + 1} for ${fieldLabel}`}
                  onChange={(header) => {
                    const next = [...source.headers];
                    if (header) next[i] = header;
                    else next.splice(i, 1);
                    onChange({ ...source, headers: next.filter(Boolean) });
                  }}
                />
              ))}
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Separator
                <Input
                  value={source.separator}
                  onChange={(e) => onChange({ ...source, separator: e.target.value })}
                  className="h-7 w-16 text-sm"
                  aria-label={`Separator for ${fieldLabel}`}
                />
              </label>
            </div>
          )}

          {source.kind === 'split' && (
            <div className="flex flex-col gap-2">
              <HeaderSelect
                headers={headers}
                value={source.header || null}
                placeholder="Column to split"
                ariaLabel={`Column to split for ${fieldLabel}`}
                onChange={(header) => {
                  if (header) onChange({ ...source, header });
                }}
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Split on
                <Input
                  value={source.delimiter}
                  onChange={(e) => onChange({ ...source, delimiter: e.target.value })}
                  className="h-7 w-16 text-sm"
                  aria-label={`Delimiter for ${fieldLabel}`}
                />
              </label>
              <Select
                value={source.takeRemainder ? 'remainder' : String(Math.min(source.partIndex, 2))}
                onValueChange={(v) => {
                  if (v === 'remainder') {
                    onChange({ ...source, partIndex: 1, takeRemainder: true });
                  } else {
                    onChange({ ...source, partIndex: Number(v), takeRemainder: false });
                  }
                }}
              >
                <SelectTrigger aria-label={`Which part for ${fieldLabel}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">1st part</SelectItem>
                  <SelectItem value="1">2nd part</SelectItem>
                  <SelectItem value="2">3rd part</SelectItem>
                  <SelectItem value="remainder">Everything after the 1st part</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {sampleRow && source.kind !== 'none' && (
            <p className="rounded bg-muted/40 px-2 py-1 text-xs">
              Example from your first row:{' '}
              <span className="font-medium">{example || <em>(empty)</em>}</span>
            </p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
